import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { UnitToggle, TextField, NotesField, u } from "./_shared";
import { BHA_COMPONENT_KINDS } from "@/lib/profiles/schemas";
import { listByKind, LIB_VERSION } from "@/lib/reference/tubularPickers";

// BHA component → reference-library type mapping.
const KIND_TO_LIB = {
  "Drill Pipe":   "drill_pipe",
  "Drill Collar": "drill_collar",
  "HWDP":         "hwdp",
  "Mud Motor":    "mud_motor",
  "RSS":          "rss",
  "MWD":          "mwd",
  "LWD":          "lwd",
  "Stabilizer":   "stabilizer",
  "Crossover":    "crossover",
  "Bit":          "bit",
};

const _emptyComponent = () => ({
  kind: "Drill Collar", length: "", odIn: "", idIn: "",
  weightLbFt: "", grade: "", libId: "",
});

export default function BHAProfileForm({ value, onChange }) {
  const units = value.units || "IMP";
  const set = (patch) => onChange({ ...value, ...patch });
  const setComponents = (components) => set({ components });

  const updateRow = (i, patch) => {
    const next = value.components.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    setComponents(next);
  };
  const addRow = () => setComponents([...(value.components || []), _emptyComponent()]);
  const removeRow = (i) =>
    setComponents(value.components.length <= 1
      ? value.components
      : value.components.filter((_, idx) => idx !== i));

  const totalLength = (value.components || [])
    .map((c) => parseFloat(c.length))
    .filter((n) => Number.isFinite(n))
    .reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col gap-4" data-testid="bha-profile-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField value={value} onChange={onChange} field="label" label="Profile label"
                   placeholder="e.g. BHA Run #3 — 12¼ in section" testId="bha-label" />
        <TextField value={value} onChange={onChange} field="rig" label="Rig (optional)"
                   placeholder="e.g. Rig Alpha" testId="bha-rig" />
        <UnitToggle value={units} onChange={(v) => set({ units: v })} testId="bha-units" />
      </div>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Component stack-up (top to bit)
        </legend>
        <div className="overflow-x-auto rounded-md" style={{ border: "1px solid var(--rs-border)" }}>
          <table className="min-w-full text-[11.5px]" data-testid="bha-components-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.10em] text-[var(--rs-text-3)]">
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Kind</th>
                <th className="text-left p-2">Length ({u.depth(units)})</th>
                <th className="text-left p-2">OD ({u.dia(units)})</th>
                <th className="text-left p-2">ID ({u.dia(units)})</th>
                <th className="text-left p-2">Library</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {(value.components || []).map((c, i) => {
                const libKind = KIND_TO_LIB[c.kind];
                return (
                  <tr key={i} data-testid={`bha-row-${i}`} className="border-t" style={{ borderColor: "rgba(148,163,184,0.18)" }}>
                    <td className="p-2 font-mono text-[var(--rs-text-3)] align-top">{i + 1}</td>
                    <td className="p-2 align-top">
                      <select
                        data-testid={`bha-row-${i}-kind`}
                        value={c.kind}
                        onChange={(e) => updateRow(i, { kind: e.target.value, libId: "" })}
                        className="input-rs text-[12px] min-w-[140px]"
                        style={{ height: 36 }}
                      >
                        {BHA_COMPONENT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </td>
                    <td className="p-2 align-top">
                      <input type="number" step="0.1" data-testid={`bha-row-${i}-length`}
                        value={c.length} onChange={(e) => updateRow(i, { length: e.target.value })}
                        className="input-rs text-[12px] min-w-[100px]" style={{ height: 36 }} />
                    </td>
                    <td className="p-2 align-top">
                      <input type="number" step="0.01" data-testid={`bha-row-${i}-od`}
                        value={c.odIn} onChange={(e) => updateRow(i, { odIn: e.target.value })}
                        className="input-rs text-[12px] min-w-[80px]" style={{ height: 36 }} />
                    </td>
                    <td className="p-2 align-top">
                      <input type="number" step="0.01" data-testid={`bha-row-${i}-id`}
                        value={c.idIn} onChange={(e) => updateRow(i, { idIn: e.target.value })}
                        className="input-rs text-[12px] min-w-[80px]" style={{ height: 36 }} />
                    </td>
                    <td className="p-2 align-top">
                      {libKind ? (
                        <LibrarySelect
                          libKind={libKind}
                          currentLibId={c.libId}
                          onPick={(it) => updateRow(i, {
                            libId: it.id,
                            odIn:  it.od_in != null ? String(it.od_in) : c.odIn,
                            idIn:  it.id_in != null ? String(it.id_in) : c.idIn,
                            weightLbFt: it.weight_lbft != null ? String(it.weight_lbft) : c.weightLbFt,
                            grade: it.grade != null ? String(it.grade) : (c.grade || ""),
                          })}
                          onClear={() => updateRow(i, { libId: "" })}
                          testId={`bha-row-${i}-lib`}
                        />
                      ) : (
                        <span className="text-[10px] text-[var(--rs-text-3)]">—</span>
                      )}
                    </td>
                    <td className="p-2 align-top text-right">
                      <button type="button" onClick={() => removeRow(i)}
                              data-testid={`bha-row-${i}-remove`}
                              disabled={value.components.length <= 1}
                              className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5] disabled:opacity-25"
                              title="Remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t" style={{ borderColor: "var(--rs-border)" }}>
                <td colSpan={2} className="p-2 text-right text-[var(--rs-text-3)] uppercase tracking-wider text-[10px]">Total length</td>
                <td className="p-2 font-mono text-amber-400 font-bold" data-testid="bha-total-length">
                  {totalLength.toFixed(1)} {u.depth(units)}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <button type="button" onClick={addRow} data-testid="bha-add-row"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px]"
                style={{ background: "rgba(245,180,0,0.10)", color: "var(--rs-amber)", border: "1px solid rgba(245,180,0,0.35)", minHeight: 32 }}>
          <Plus className="w-4 h-4" /> Add component
        </button>
      </fieldset>

      <NotesField value={value} onChange={onChange} testId="bha-notes" />
    </div>
  );
}

function LibrarySelect({ libKind, currentLibId, onPick, onClear, testId }) {
  const items = React.useMemo(() => listByKind(libKind), [libKind]);
  // Build dropdown options once per kind; first option = "Manual entry".
  return (
    <div className="flex flex-col gap-1">
      <select
        data-testid={testId}
        value={currentLibId || ""}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) { onClear && onClear(); return; }
          const hit = items.find((x) => x.id === id);
          if (hit) onPick(hit);
        }}
        className="input-rs text-[11px] min-w-[170px]"
        style={{ height: 36 }}
      >
        <option value="">Manual entry…</option>
        {items.map((it) => (
          <option key={it.id} value={it.id}>{it.label}</option>
        ))}
      </select>
      {currentLibId && (
        <span data-testid={`${testId}-provenance`}
              className="text-[9.5px] px-1.5 py-0.5 rounded-md self-start"
              style={{
                background: "rgba(245,180,0,0.10)",
                color: "var(--rs-amber)",
                border: "1px solid rgba(245,180,0,0.35)",
              }}>
          {LIB_VERSION} #{currentLibId}
        </span>
      )}
    </div>
  );
}

function LibraryPicker({ libKind, currentLibId, onPick, testId }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const items = React.useMemo(() => listByKind(libKind), [libKind]);
  const filtered = q.trim()
    ? items.filter((it) => (it.label + " " + it.id).toLowerCase().includes(q.trim().toLowerCase()))
    : items;
  return (
    <div className="flex flex-col gap-1">
      <button type="button" data-testid={testId} onClick={() => setOpen((v) => !v)}
              className="text-[10.5px] px-2 py-1 rounded-md whitespace-nowrap"
              style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 32 }}>
        {open ? "Close" : "Pick"}
      </button>
      {currentLibId && (
        <span data-testid={`${testId}-provenance`} className="text-[9.5px] px-1.5 py-0.5 rounded-md self-start"
              style={{ background: "rgba(245,180,0,0.10)", color: "var(--rs-amber)", border: "1px solid rgba(245,180,0,0.35)" }}>
          {LIB_VERSION} #{currentLibId}
        </span>
      )}
      {open && (
        <div data-testid={`${testId}-picker`} className="card-rs p-2 flex flex-col gap-1" style={{ background: "rgba(10,11,14,0.85)" }}>
          <input data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search…" className="input-rs text-[11px]" style={{ minHeight: 28 }} />
          <div style={{ maxHeight: 180, overflowY: "auto" }} className="flex flex-col gap-1">
            {filtered.length === 0 ? (
              <div className="text-[10.5px] text-[var(--rs-text-3)] px-1">No matches</div>
            ) : filtered.map((it) => (
              <button key={it.id} type="button" data-testid={`${testId}-row-${it.id}`}
                      onClick={() => { onPick(it); setOpen(false); setQ(""); }}
                      className="text-left text-[10.5px] rounded px-1.5 py-1"
                      style={{ background: "rgba(22,24,29,0.6)", border: "1px solid var(--rs-border)", color: "var(--rs-text-1)" }}>
                <span style={{ color: "var(--rs-amber)" }}>{it.id}</span>
                <span className="text-[var(--rs-text-2)]"> · {it.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
